import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import PortalScene from './PortalScene';
import PortalTransit from './PortalTransit';
import CoreAccessIcon from './CoreAccessIcon';
import LastSignalCinematic from './LastSignalCinematic';
import { BOSS_MAX_HP } from './corePhases';
import { getMaxHp } from './game';
import { BoardConditionBadge } from './BoardConditionNotice';
import { getRoomBoardCondition } from './boardConditions';
import { lockBodyScroll } from '../../utils/bodyScrollLock';
import { playSound } from '../../utils/utils';
import { CORE_PORTAL_ENTRY_SOUND } from '../../utils/soundEffects';
import './PortalChamber.css';
import './CoreAccess.css';
import { usePopupTransition } from './popupTransition';

const CORE_GATE = {
  id: 'core-gate',
  kind: 'Core',
  name: 'The Glitch Core',
  color: '#c58bff',
  locked: false,
  routeClass: 'core',
};
const CORE_ROUTES = [CORE_GATE];

const CoreAccess = ({ game, reduceMotion, onEnter, children }) => {
  const [marketOpen, setMarketOpen] = useState(true);
  const { close: closeMarket, popupClass: marketPopupClass } =
    usePopupTransition(() => setMarketOpen(false), {
      open: marketOpen,
      reduceMotion,
    });
  const [entering, setEntering] = useState(false);
  const [arrived, setArrived] = useState(false);
  const [active, setActive] = useState(false);
  const entryRef = useRef(false);
  const marketTrigger = useRef(null);
  const marketDialog = useRef(null);
  const marketClose = useRef(null);
  useEffect(() => {
    if (!marketOpen) return undefined;
    const release = lockBodyScroll();
    const root = document.getElementById('root');
    const wasInert = root?.inert;
    const trigger = marketTrigger.current;
    if (root) root.inert = true;
    marketClose.current?.focus({ preventScroll: true });
    return () => {
      release();
      if (root) root.inert = wasInert;
      trigger?.focus({ preventScroll: true });
    };
  }, [marketOpen]);
  const enter = () => {
    if (entryRef.current || marketOpen) return;
    entryRef.current = true;
    playSound('/sounds/die-pre-entering-portal.mp3', { preventOverlap: true });
    if (reduceMotion) {
      playSound(CORE_PORTAL_ENTRY_SOUND, { preventOverlap: true });
      setArrived(true);
    } else setEntering(true);
  };
  const trapMarketFocus = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      closeMarket();
    }
    if (event.key !== 'Tab') return;
    const controls = [
      ...marketDialog.current.querySelectorAll(
        'button:not(:disabled), a[href], [tabindex="0"]'
      ),
    ];
    const first = controls[0];
    const last = controls[controls.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first?.focus();
    }
  };
  return (
    <section className="coreboundPanel coreboundCoreAccess">
      <header className="coreAccessHeading">
        <span className="coreboundPanelKicker">SECTOR 10 / CORE ACCESS</span>
        <span className="coreAccessLastSignal">FINAL SIGNAL</span>
        <h2>The network ends here.</h2>
        <p>Beyond this gate, the source of the corruption is waiting.</p>
      </header>
      <div className="coreAccessChamber">
        <button
          type="button"
          className="coreAccessGate"
          aria-label="Enter the Core arena"
          onClick={enter}
          onMouseEnter={() => setActive(true)}
          onMouseLeave={() => setActive(false)}
          onFocus={() => setActive(true)}
          onBlur={() => setActive(false)}
          disabled={entering || arrived}
        >
          <PortalScene
            routes={CORE_ROUTES}
            activeId={active ? CORE_GATE.id : null}
            reduceMotion={reduceMotion}
          />
          <span className="coreAccessGateDesignation" aria-hidden="true">
            ORIGIN SIGNAL / 010
          </span>
          <span className="coreAccessGateCaption" aria-hidden="true">
            <small>THE FINAL BREACH</small>
            <strong>
              ENTER THE CORE <CoreAccessIcon />
            </strong>
          </span>
        </button>
        <div className="coreAccessBriefing">
          <span className="coreAccessThreat">CORE SIGNAL DETECTED</span>
          <h3>The Glitch Core</h3>
          <p className="coreAccessBossHealth">
            <strong>{BOSS_MAX_HP.toLocaleString()}</strong> HP{' '}
            <span>EVOLVING ENTITY</span>
          </p>
          <p className="coreAccessBrief">
            Every strike chips it down. Choose Strike, Guard, or Overload to
            balance die power and incoming damage.
          </p>
          <BoardConditionBadge
            condition={getRoomBoardCondition(game, 'boss')}
          />
          <div className="coreAccessLoadout" aria-label="Your preparation">
            <span>
              <small>HEALTH</small>
              <b>
                {game.hp} / {getMaxHp(game)}
              </b>
            </span>
            <span>
              <small>SHARDS</small>
              <b>{game.shards}</b>
            </span>
            <span>
              <small>RELICS</small>
              <b>{game.relics.length}</b>
            </span>
          </div>
          <button
            ref={marketTrigger}
            type="button"
            className="coreAccessMarketTrigger"
            onClick={() => setMarketOpen(true)}
            aria-haspopup="dialog"
            aria-expanded={marketOpen}
            disabled={entering}
          >
            <span>
              Final Signal Exchange<small>Relics & repairs</small>
            </span>
            <CoreAccessIcon variant="exchange" />
          </button>
          <p className="coreAccessWarning">
            Make your final preparations. Entering seals the exchange.
          </p>
        </div>
      </div>
      <footer className="coreAccessFooter">
        <span>NINE SECTORS BEHIND YOU.</span>
        <strong>ONE CORE LEFT.</strong>
      </footer>
      {marketOpen &&
        createPortal(
          <div
            className="coreAccessMarketBackdrop"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) closeMarket();
            }}
          >
            <motion.section
              ref={marketDialog}
              className={`portalMarketDialog coreAccessMarketDialog ${marketPopupClass}`}
              layout={!reduceMotion}
              transition={{
                layout: { duration: reduceMotion ? 0 : 0.28, ease: 'easeOut' },
              }}
              role="dialog"
              aria-modal="true"
              aria-labelledby="coreAccessMarketTitle"
              onKeyDown={trapMarketFocus}
            >
              <div className="portalMarketDialogHeader">
                <span>FINAL STOP / SIGNAL EXCHANGE</span>
                <span className="portalMarketWallet" role="status">
                  Available shards: <strong>{game.shards}</strong>
                </span>
                <button
                  ref={marketClose}
                  type="button"
                  onClick={closeMarket}
                  aria-label="Close final Signal Exchange"
                >
                  Close ×
                </button>
              </div>
              <div className="coreboundShop coreAccessMarketBody">
                {children}
              </div>
            </motion.section>
          </div>,
          document.body
        )}
      {entering && !arrived && (
        <PortalTransit
          route={CORE_GATE}
          dieId={game.selectedDie}
          floor={10}
          skipLabel="Skip portal travel"
          onComplete={() => setArrived(true)}
        />
      )}
      {arrived && (
        <LastSignalCinematic
          variant="arrival"
          dieId={game.selectedDie}
          reduceMotion={reduceMotion}
          faceValues={game.powerUp?.faceValues}
          onComplete={onEnter}
        />
      )}
    </section>
  );
};

export default CoreAccess;
